/**
 * Regras do Menu Resolução que o tablet decide sozinho (docs/menuresolucao-integracao.md;
 * plano `tablet-frontend`, entregas TF9 e TF10). Módulo puro: sem Phaser, sem rede, sem
 * relógio global, para que a lógica do carimbo e da outbox seja testável.
 *
 * O servidor é a fonte da verdade: estas funções só decidem o que o tablet mostra e o que
 * envia. Pontos, veredito e janela são sempre recalculados no servidor.
 */

/** Os cinco Pixels do módulo, em ordem (o Extra é o 5). */
export const PIXELS = Object.freeze([1, 2, 3, 4, 5]);

/** Pixel 3 aceita "até" a quantidade exibida; os demais exigem a quantidade exata. */
export const PIXEL_TRES = 3;

/** Pixels 1, 2, 4 e Extra: `quantidade_selecao` exata por pergunta (§7.3). */
export function exigeQuantidadeExata (pixel) {
    return pixel !== PIXEL_TRES;
}

/**
 * Corrige o relógio do tablet com `agora_servidor` (§6.2).
 * `deslocamentoMs` = relógio do servidor − relógio local, no instante da resposta.
 */
export function deslocamentoDoRelogio (agoraServidorIso, relogioLocalMs) {
    const servidorMs = Date.parse(agoraServidorIso);
    if (Number.isNaN(servidorMs)) return 0;
    return servidorMs - relogioLocalMs;
}

/** Instante "do servidor" segundo o relógio local corrigido. */
export function instanteCorrigido (relogioLocalMs, deslocamentoMs = 0) {
    return relogioLocalMs + deslocamentoMs;
}

/**
 * Segundos restantes até `resposta_ate`, já corrigidos (§6.2, passo 2). Nunca negativo.
 */
export function tempoRestanteSegundos ({ respostaAte, relogioLocalMs, deslocamentoMs = 0 }) {
    const limiteMs = Date.parse(respostaAte);
    if (Number.isNaN(limiteMs)) return 0;
    const restanteMs = limiteMs - instanteCorrigido(relogioLocalMs, deslocamentoMs);
    return Math.max(0, Math.ceil(restanteMs / 1000));
}

/**
 * Carimbo de conclusão (§6.2, passo 3): o instante do "Enviar" já corrigido, em ISO UTC.
 * É o `concluido_em` do sync e o que decide se a resposta vale (§6.3).
 */
export function carimboDeConclusao (relogioNoEnviarMs, deslocamentoMs = 0) {
    return new Date(instanteCorrigido(relogioNoEnviarMs, deslocamentoMs)).toISOString();
}

/** Janela de um Pixel dentro do `GET /estado` (§4.3). Ausente = não aberta. */
export function janelaDoPixel (estado, pixel) {
    return (estado?.pixels ?? []).find((p) => p.pixel === pixel) ?? {
        pixel, rodada: null, janela: 'nao_aberta', aberta_em: null, resposta_ate: null,
        fecha_em: null, encerrada_em: null, encerrada_por: null,
        resultado_liberado: false, resultado_liberado_em: null
    };
}

/** O conteúdo de um Pixel só é baixado com a janela dele aberta (§5, RN-MR-061). */
export function podeBaixarConteudo (estado, pixel) {
    return estado?.fase === 'aberta' && janelaDoPixel(estado, pixel).janela === 'aberta';
}

/**
 * Descarta a rodada local quando a vigente é outra (reinício, §6.4). Sem rodada local,
 * nada a descartar.
 */
export function rodadaDescartavel (rodadaLocal, rodadaVigente) {
    return rodadaLocal != null && rodadaVigente != null && rodadaLocal !== rodadaVigente;
}

/**
 * Valida a seleção do participante antes de entrar na outbox (§7.3 e §7.4).
 * `perguntas`: [{ id, quantidade_selecao, opcoes:[{id}] }] do conteúdo do Pixel.
 * `respostas`: [{ pergunta_id, opcao_id }], uma entrada por seleção.
 * Pixel 3: `quantidadeExibida` é o número de situações sorteadas; menos é aceito.
 * Devolve { ok, erros } com os mesmos códigos do servidor quando houver.
 */
export function validarSelecao ({ pixel, perguntas = [], respostas = [], quantidadeExibida = null }) {
    const erros = [];
    const porPergunta = new Map(perguntas.map((p) => [p.id, p]));
    const contagem = new Map();
    const vistas = new Set();

    for (const r of respostas) {
        const pergunta = porPergunta.get(r.pergunta_id);
        if (!pergunta) { erros.push('pergunta_fora_do_pixel'); continue; }
        if (!pergunta.opcoes.some((o) => o.id === r.opcao_id)) { erros.push('opcao_fora_da_pergunta'); continue; }
        const chave = `${r.pergunta_id}|${r.opcao_id}`;
        if (vistas.has(chave)) erros.push('alternativa_repetida');
        vistas.add(chave);
        contagem.set(r.pergunta_id, (contagem.get(r.pergunta_id) ?? 0) + 1);
    }

    if (exigeQuantidadeExata(pixel)) {
        for (const p of perguntas) {
            if ((contagem.get(p.id) ?? 0) !== p.quantidade_selecao) {
                erros.push('quantidade_de_selecao_invalida');
                break;
            }
        }
    } else {
        for (const [, n] of contagem) {
            if (n > 1) { erros.push('pergunta_repetida'); break; }
        }
        if (quantidadeExibida != null && respostas.length > quantidadeExibida) erros.push('quantidade_excedida');
    }

    return { ok: erros.length === 0, erros: [...new Set(erros)] };
}

/**
 * Habilitação do botão Enviar (RI-MR-03): só com a quantidade exata (Pixels 1, 2, 4 e Extra).
 * No Pixel 3 o botão habilita quando cada situação tem a sua resposta.
 */
export function enviarHabilitado ({ pixel, perguntas = [], respostas = [] }) {
    if (!exigeQuantidadeExata(pixel)) {
        return perguntas.every((p) => respostas.some((r) => r.pergunta_id === p.id));
    }
    return validarSelecao({ pixel, perguntas, respostas }).ok;
}

/**
 * Chave de idempotência do sync: uma por Pixel e por rodada (§7.1). A chave precisa ser
 * a mesma em todo retry, por isso `uuid` vem de fora e é guardado junto do payload na outbox.
 */
export function chaveIdempotencia ({ pixel, rodada, uuid }) {
    if (!uuid) throw new Error('chaveIdempotencia: uuid é obrigatório');
    return `menuresolucao-p${pixel}-r${rodada}-${uuid}`;
}

/** Corpo do `POST .../pixels/{pixel}/resposta` (§7.2). Sem pontos, sem veredito, sem ids do jogador. */
export function montarSync ({ rodada, concluidoEm, respostas = [] }) {
    return {
        rodada,
        concluido_em: concluidoEm,
        respostas: respostas.map((r) => ({ pergunta_id: r.pergunta_id, opcao_id: r.opcao_id }))
    };
}

/**
 * Reação da outbox a uma resposta do servidor ao sync (§7.7). Devolve uma ação:
 * - 'remover'          : aceito ou já recebido; sai da outbox
 * - 'manter'           : tenta de novo com a MESMA chave (rede, timeout, 5xx, fase fechada)
 * - 'nova_chave'       : a chave foi reaproveitada por engano; gera outra e reenvia
 * - 'reautenticar'     : 401; reautentica e reenvia com a MESMA chave
 * - 'erro_permanente'  : registra e reporta, sem loop
 */
export function reagirAoResultadoDoSync ({ status, codigo = null }) {
    if (status == null || status === 0) return 'manter';
    if (status === 202) return 'remover';
    if (status === 401) return 'reautenticar';
    if (status >= 500) return 'manter';
    if (status === 409) {
        if (codigo === 'resposta_ja_enviada') return 'remover';
        if (codigo === 'fase_nao_aberta') return 'manter';
        if (codigo === 'idempotency_key_de_outra_origem') return 'nova_chave';
        return 'erro_permanente';
    }
    return 'erro_permanente';
}

/**
 * Tela "Resolução completa" (§9.2 e §9.3). Monta só com o que `minha-pontuacao` entrega.
 * Nunca soma os pontos individuais e grupais num total: o Pixel 3 é individual e os
 * Pixels 1, 2 e 4 são do grupo. Antes da consolidação, o grupo aparece como "aguardando",
 * nunca como zero.
 */
export function montarResolucaoFinal (minhaPontuacao) {
    const p3 = minhaPontuacao?.pixel_3 ?? null;
    const pixelsGrupo = (minhaPontuacao?.grupo?.pixels ?? []).map((p) => ({
        pixel: p.pixel,
        pontos: p.pontos ?? null,
        aguardando: p.pontos == null
    }));
    const totalGrupo = minhaPontuacao?.grupo?.total ?? null;

    return {
        pixel3: {
            status: p3?.status ?? 'nao_enviado',
            pontuacao: p3?.status === 'processado' ? p3.pontuacao : null,
            aguardando: p3?.status !== 'processado'
        },
        grupo: {
            pixels: pixelsGrupo,
            total: totalGrupo,
            aguardando: totalGrupo == null
        }
    };
}

/**
 * Pontos do grupo num Pixel (1, 2 ou 4) pela participação: % de membros com resposta válida
 * na janela (docs/menuresolucao-integracao.md §9.1). `[0,50)` = 10, `[50,100)` = 15, 100 = 20;
 * zero respondentes = 0. No servidor isto é recalculado; aqui serve ao modo `dev`.
 */
export function pontosPorParticipacao (percentual) {
    if (!(percentual > 0)) return 0;
    if (percentual >= 100) return 20;
    return percentual >= 50 ? 15 : 10;
}

/** Pixel 3, individual e tudo ou nada: todas as afirmações certas = 20; qualquer erro ou omissão = 0. */
export function pontuacaoPixel3 (respostas, gabarito) {
    const completo = Array.isArray(respostas) && Array.isArray(gabarito)
        && gabarito.length > 0 && respostas.length === gabarito.length;
    return completo && gabarito.every((certa, i) => respostas[i] === certa) ? 20 : 0;
}

/* ------------------------------------------------------------------------------------------
 * Tradução do contrato do servidor para o que as cenas já usam (modo `prd`).
 * A cena do jogador foi feita sobre um "painel" simples (pixel, rodada, janela, catálogo,
 * resultados); estas funções montam esse painel a partir de `GET /estado`, `/conteudo` e
 * `/resultado`, sem acrescentar regra nenhuma: pontos e veredito seguem sendo do servidor.
 * ------------------------------------------------------------------------------------------ */

/** Títulos de reserva, para o caso de `GET /abertura` não ter respondido. */
const TITULOS_RESERVA = Object.freeze({ 1: 'Mercado', 2: 'Cliente', 3: 'Profissional', 4: 'A força da Fiat', 5: 'Extra' });

/** "Pixel 1 — Mercado (Radar de atenção)" → "Mercado": o que a cena mostra depois de "PIXEL 1/4 - ". */
export function tituloDoPixel (titulo, pixel = null) {
    const texto = String(titulo ?? '').trim();
    if (!texto) return TITULOS_RESERVA[pixel] ?? '';
    const [, ...depois] = texto.split(/\s[—–-]\s/);
    const miolo = depois.length > 0 ? depois.join(' — ') : texto;
    return miolo.replace(/\s*\([^)]*\)\s*$/, '').trim();
}

/**
 * O Pixel que a tela do jogador acompanha: o da janela aberta; sem ela, o último que foi aberto
 * (o facilitador pode pular e repetir Pixels, então a ordem do número não vale). Nenhum aberto = `null`.
 */
export function pixelEmFoco (estado) {
    const abertos = (estado?.pixels ?? []).filter((p) => p.rodada != null && p.janela !== 'nao_aberta');
    if (abertos.length === 0) return null;
    const aberta = abertos.find((p) => p.janela === 'aberta');
    if (aberta) return aberta;
    return abertos.reduce((a, b) => (Date.parse(b.aberta_em) > Date.parse(a.aberta_em) ? b : a));
}

/** Percentual do servidor (texto com 1 casa, "18.0") → como a tela mostra: "18" e "23,1". */
export function formatarPercentual (texto) {
    const numero = Number(texto);
    if (!Number.isFinite(numero)) return String(texto ?? '');
    return Number.isInteger(numero) ? String(numero) : numero.toFixed(1).replace('.', ',');
}

/**
 * `GET /pixels/{n}/conteudo` → catálogo da cena. Nos Pixels 1, 2, 4 e Extra há uma pergunta com as
 * alternativas (a cena marca por `id` e mostra a `letra`); o Pixel 3 vira a lista de afirmações, na
 * ordem que o servidor sorteou para este participante.
 */
export function paraCatalogoMR ({ pixel, titulo, conteudo }) {
    const perguntas = conteudo?.perguntas ?? [];
    if (pixel === PIXEL_TRES) {
        return { titulo, surpresa: true, afirmacoes: perguntas.map((p) => p.enunciado) };
    }
    const [pergunta] = perguntas;
    return {
        titulo,
        enunciado: pergunta?.enunciado ?? '',
        quantidade_selecao: pergunta?.quantidade_selecao ?? 1,
        opcoes: (pergunta?.opcoes ?? []).map((o) => ({ id: o.id, letra: o.letra, texto: o.texto }))
    };
}

/** `GET /pixels/{n}/resultado` → listas da cena (`rotulo` + `percentual`), na ordem do servidor. */
export function paraResultadoMR (resposta, grupoId = null) {
    const lista = (agregado) => (agregado?.alternativas ?? [])
        .map((a) => ({ rotulo: a.texto, percentual: formatarPercentual(a.percentual) }));
    return { turma: lista(resposta?.turma), grupos: grupoId ? { [grupoId]: lista(resposta?.grupo) } : {} };
}

/**
 * Resultado do Pixel 3 como a tela mostra: cada afirmação com o percentual de CONTROLO e o de NÃO
 * CONTROLO. O servidor só manda o de CONTROLO (por afirmação, sobre as respostas válidas dela); o outro
 * é o que sobra para 100, já que cada resposta é uma ou outra. Mantém a ordem do servidor. Percentual
 * ilegível sai vazio nos dois, em vez de inventar um número.
 *
 * @param {{rotulo: string, percentual: string}[]} lista saída de `paraResultadoMR` (percentual já formatado)
 */
export function paraAfirmacoesDoPixel3 (lista = []) {
    return lista.map(({ rotulo, percentual }) => {
        const texto = String(percentual ?? '').trim().replace(',', '.');
        // `Number('')` é 0: vazio tem de contar como ilegível, não como 0%.
        const controlo = texto === '' ? Number.NaN : Number(texto);
        const legivel = Number.isFinite(controlo);
        return {
            afirmacao: rotulo,
            controlo: legivel ? formatarPercentual(percentual) : '',
            naoControlo: legivel ? formatarPercentual(Number((100 - controlo).toFixed(1))) : ''
        };
    });
}

const semAcento = (texto) => String(texto ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();

/** Id da alternativa CONTROLO ou NÃO CONTROLO de uma situação do Pixel 3 (pelo texto, não pela letra). */
export function opcaoDoPixel3 (pergunta, valor) {
    const alvo = valor === 'controlo' ? 'CONTROLO' : 'NAO CONTROLO';
    return (pergunta?.opcoes ?? []).find((o) => semAcento(o.texto) === alvo)?.id ?? null;
}

/**
 * Respostas do Pixel 3 (`'controlo' | 'nao_controlo'` por situação, na ordem exibida) → seleções do
 * sync. Situação sem resposta sai de fora: o servidor conta omissão como erro (§7.3).
 */
export function respostasDoPixel3 (perguntas = [], respostas = []) {
    return perguntas.flatMap((pergunta, i) => {
        const opcaoId = respostas[i] ? opcaoDoPixel3(pergunta, respostas[i]) : null;
        return opcaoId ? [{ pergunta_id: pergunta.id, opcao_id: opcaoId }] : [];
    });
}
